import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ENCRYPTED_TABLES,
  SERVER_ON_REQUEST,
  SERVER_ONLY_KINDS,
  readableByReason,
  serverReadReasons,
} from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { readEncryptionState } from '../crypto/e2e.js';
import type { EncryptionState } from '../crypto/e2e.js';
import { readHouseholdsPending } from '../crypto/householdKeys.js';
import { useIsChild } from '../hooks/useModules.js';
import { useSwipeBack } from '../gestures/SwipeProvider.js';

/**
 * Kachel in „Privatsphäre“: der Eingang zur Seite „Was der Server sieht“.
 */
export function ServerViewTile({ onOpen }: { onOpen: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="server-view-tile" className="kw-tile flex flex-col gap-2">
      <h3 id="server-view-tile" className="kw-kicker">
        {t('serverView.heading')}
      </h3>
      <p className="text-sm text-ink-muted">{t('serverView.tileIntro')}</p>
      <button type="button" onClick={onOpen} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('serverView.open')}
      </button>
    </section>
  );
}

/**
 * „Was der Server sieht“ (27.10.2026): die ehrliche Liste der Ausnahmen von
 * der Ende-zu-Ende-Verschlüsselung. Alles steht im Register
 * (`SEALED_EMPTY`, `SERVER_READABLE`, `SERVER_ONLY_TABLES`) — diese Seite
 * rendert nur daraus, damit sie nicht als Fließtext veraltet.
 */
export function ServerView({
  onClose,
  onSetup,
}: {
  onClose: () => void;
  /** Zurück zur Kachel „Verschlüsselung“ — dort wird eingerichtet und entsperrt. */
  onSetup: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  useSwipeBack(onClose);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const child = useIsChild();
  const state = useLiveQuery(() => readEncryptionState(db), [], null as EncryptionState | null);
  const householdsWaiting = useLiveQuery(() => readHouseholdsPending(db), [], 0);

  // Neue Ansicht: Fokus auf die Überschrift, damit Tastatur und Vorlesen oben anfangen.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const reasonTitle = (reason: string) => t(`serverView.readable.reasons.${reason}.title`);

  return (
    <section aria-labelledby="server-view-heading" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2
          id="server-view-heading"
          ref={headingRef}
          tabIndex={-1}
          className="font-display text-xl font-bold uppercase"
        >
          {t('serverView.heading')}
        </h2>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small px-3">
          {t('serverView.back')}
        </button>
      </div>
      <p className="text-sm text-ink-muted">{t('serverView.intro')}</p>

      <section aria-labelledby="server-view-status" className="kw-tile flex flex-col gap-2">
        <h3 id="server-view-status" className="kw-kicker">
          {t('serverView.status.heading')}
        </h3>
        {state === null ? null : state === 'ready' ? (
          <p className="text-sm text-good">{t('serverView.status.ready')}</p>
        ) : (
          <p className="text-sm text-warn">
            {state === 'locked'
              ? t('serverView.status.locked')
              : child
                ? t('serverView.status.offChild')
                : t('serverView.status.off')}
          </p>
        )}
        {householdsWaiting > 0 ? (
          <p className="text-sm text-warn">{t('serverView.status.household')}</p>
        ) : null}
        {/* Kinder richten nicht selbst ein — ihr Gerät tut es mit dem Haushaltsschlüssel. */}
        {state !== null && state !== 'ready' && !child ? (
          <button type="button" onClick={onSetup} className="kw-btn kw-btn-small">
            {t('serverView.status.setup')}
          </button>
        ) : null}
      </section>

      <section aria-labelledby="server-view-sealed" className="kw-tile flex flex-col gap-2">
        <h3 id="server-view-sealed" className="kw-kicker">
          {t('serverView.sealed.heading')}
        </h3>
        <p className="text-sm text-ink-muted">{t('serverView.sealed.intro')}</p>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
          {ENCRYPTED_TABLES.map((table) => (
            <li key={table}>{t(`serverView.sealed.tables.${table}`)}</li>
          ))}
          <li>{t('serverView.sealed.photos')}</li>
        </ul>
      </section>

      <section aria-labelledby="server-view-readable" className="kw-tile flex flex-col gap-3">
        <h3 id="server-view-readable" className="kw-kicker">
          {t('serverView.readable.heading')}
        </h3>
        <p className="text-sm text-ink-muted">{t('serverView.readable.intro')}</p>
        {readableByReason().map(({ reason, tables }) => (
          <section
            key={reason}
            aria-labelledby={`server-view-reason-${reason}`}
            className="flex flex-col gap-1 border-t border-line pt-2"
          >
            <h4 id={`server-view-reason-${reason}`} className="kw-label">
              {reasonTitle(reason)}
            </h4>
            <p className="text-xs text-ink-muted">
              {t(`serverView.readable.reasons.${reason}.why`)}
            </p>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
              {tables.map((table) => {
                const more = serverReadReasons(table).slice(1);
                return (
                  <li key={table}>
                    {t(`serverView.readable.tables.${table}`)}
                    {more.length > 0 ? (
                      <span className="block text-xs text-ink-muted">
                        {t('serverView.readable.also', {
                          reasons: more.map(reasonTitle).join(' · '),
                        })}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </section>

      <section aria-labelledby="server-view-always" className="kw-tile flex flex-col gap-2">
        <h3 id="server-view-always" className="kw-kicker">
          {t('serverView.always.heading')}
        </h3>
        <p className="text-sm text-ink-muted">{t('serverView.always.intro')}</p>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
          <li>{t('serverView.always.envelope')}</li>
          <li>{t('serverView.always.photos')}</li>
          <li>{t('serverView.always.storage')}</li>
          {SERVER_ON_REQUEST.map((kind) => (
            <li key={kind}>{t(`serverView.always.${kind}`)}</li>
          ))}
          {SERVER_ONLY_KINDS.map((kind) => (
            <li key={kind}>{t(`serverView.always.serverOnly.${kind}`)}</li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="server-view-backups" className="kw-tile flex flex-col gap-2">
        <h3 id="server-view-backups" className="kw-kicker">
          {t('serverView.backups.heading')}
        </h3>
        <p className="text-sm">{t('serverView.backups.body')}</p>
      </section>
    </section>
  );
}
